/**
 * PipelinesPage — Heroku-style list of deployed workflows for a project.
 *
 * Mounted at /projects/:projectId/pipelines as a child of ProjectLayout
 * (so the sidebar + header + project context come from the layout). Just
 * the list content lives here.
 *
 * Data: GET /projects/<projectId>/workflows returns minimal metadata per
 * workflow (workflowType, uuid, version, updatedAt, createdAt).
 * Click a row → /projects/<projectId>/pipelines/<workflowType>.
 *
 * Reuses DataTable (the same component TokensList uses) so the look
 * matches the rest of the dashboard. No new components.
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from '@tanstack/react-query';
import { copyOutline, globeOutline } from 'ionicons/icons';
import { IonIcon } from '@ionic/react';
import DataTable from '../../components/shared/DataTable/DataTable';
import DestroyAppModal from '../../components/shared/SoloDestroyModal/SoloDestroyModal';
import MktAlertModal from '../../components/molecules/MktAlertModal/MktAlertModal';
import WorkflowDemo from '../../components/shared/GetStartedSteps/WorkflowDemo';
import McpDemo from '../../components/shared/GetStartedSteps/McpDemo';
import DemoCarousel from '../../components/shared/GetStartedSteps/DemoCarousel';
import PipelineStatus from './PipelineStatus';
import api from '../../services/api';
import { useAgents, useDeleteAgent } from '../../hooks/useAgents';
import { queryKeys } from '../../hooks/queryKeys';
import { timeAgo } from '../../utils/timeAgo';
import { projectWorkflowMarketplacePath } from '../../utils/marketplacePaths';
import LetterAvatar from '../../components/atoms/LetterAvatar/LetterAvatar';
import './PipelinesPage.css';

// Workflow logo in the NAME cell. The list API returns an ABSOLUTE `iconUrl`
// (built from the workflow's PERSISTED marketplace slug, not its possibly-
// renamed workflowType — so a renamed pipeline still shows the ORIGINAL
// template logo, and it works in local dev where /marketplace-icons/* isn't
// served locally). When iconUrl is null (CLI-deployed / custom workflow with
// no template) or the image 404s, we fall back to the shared LetterAvatar
// (generated initial-letter placeholder) — no broken-image icon.
const WorkflowIcon = ({ iconUrl, label }) => {
  const [failed, setFailed] = useState(false);
  if (!iconUrl || failed) {
    return <LetterAvatar name={label} />;
  }
  return (
    <img
      src={iconUrl}
      onError={() => setFailed(true)}
      className="pipelines-name-icon"
      alt=""
      loading="lazy"
    />
  );
};

const PipelinesPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};
  const queryClient = useQueryClient();

  // Agents (workflows) list — React-Query cached (5min staleTime). Replaces
  // the manual useEffect/useState fetch loop; navigating away and back within
  // the staleTime window reads from cache instead of refetching.
  const { data: pipelines = [], isLoading: loading, error: pipelinesError } = useAgents(projectId);

  // Delete mutation — onSuccess invalidates the project's agent LIST (the
  // deleted row leaves the table, no skeleton flash) AND removes the agent's
  // cached DETAIL (no ghost).
  const deleteAgent = useDeleteAgent(projectId);

  // Static egress IP — shown only if (a) the account has the dedicated
  // egress addon active AND (b) this project opted in. Account-scoped
  // status endpoint also returns the per-project usesDedicatedEgress flag.
  const [egressIp, setEgressIp] = useState(null);
  // Delete-confirm modal (styled, not native confirm). Holds the pipeline
  // row pending deletion; `deleting` drives the modal's busy state.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Rename modal: holds the pipeline row being renamed; `renameDraft` is the
  // editable label, `renaming` drives the busy state. Mirrors the inline
  // displayName pencil-edit on the detail page (PATCH /workflows/{uuid}).
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Surface a list-load failure as a toast (the old fetch loop did this in
  // its catch). React Query owns retries/caching + keeps last-known-good rows
  // on a refetch error; we only mirror the toast here.
  useEffect(() => {
    if (pipelinesError) {
      setToast?.({ isOpen: true, message: `Could not load agents: ${pipelinesError.message}`, color: 'danger' });
    }
  }, [pipelinesError, setToast]);

  useEffect(() => {
    // Best-effort egress lookup. Two independent reads:
    //   (a) GET /addons/dedicated-egress/status   — does the account have
    //       the addon active + an EIP allocated?
    //   (b) GET /projects/<id>                    — does THIS project opt
    //       into using the EIP?
    //
    // We read them separately rather than relying on the joined
    // `status.projects[]` because the dedicated-egress handler's account
    // lookup uses principalId-as-accountId, while the projects table has
    // a different accountId field — the join silently returns empty in
    // that case. Reading the project record directly avoids the join
    // mismatch entirely.
    //
    // Failures (no addon, free plan, network) are silently ignored — the
    // pill just doesn't appear. Don't block the list on this.
    (async () => {
      try {
        const [status, projResp] = await Promise.all([
          api.get('/addons/dedicated-egress/status'),
          api.get(`/projects/${projectId}`),
        ]);
        const proj = projResp?.project || projResp;
        const isActive = status?.enabled && status?.status === 'active' && status?.elasticIp;
        const optedIn  = !!proj?.usesDedicatedEgress;
        if (isActive && optedIn) setEgressIp(status.elasticIp);
      } catch { /* silent — pill just doesn't render */ }
    })();
  }, [projectId]);

  // ── columns ────────────────────────────────────────────────────────
  // 4 columns deliberately. STATUS is derived (we don't have bundleStatus
  // in the list response — server returns it on the detail endpoint). For
  // now: any deployed workflow with version >= 1 is "Ready". Builds in
  // progress show on the detail page where we have richer data.
  const columns = [
    {
      header: 'NAME',
      key: 'workflowType',
      className: 'pipelines-col-name',
      width: '46%',
      skeleton: 'avatar',
      render: (p) => {
        const label = p.displayName || p.workflowType;
        // Show the raw slug as a muted secondary line ONLY when the pipeline
        // has been renamed (label differs from the slug) — otherwise it's
        // redundant noise.
        const renamed = p.displayName && p.displayName !== p.workflowType;
        return (
          <span className="pipelines-name-cell">
            <WorkflowIcon iconUrl={p.iconUrl} label={label} />
            <span className="pipelines-name-text">
              <span className="pipelines-name">{label}</span>
              {renamed && <span className="pipelines-name-slug">{p.workflowType}</span>}
            </span>
          </span>
        );
      },
    },
    {
      header: 'STATUS',
      key: 'status',
      className: 'pipelines-col-status',
      width: '18%',
      skeleton: 'status',
      // Shared <PipelineStatus> atom — same component renders this
      // cell AND the detail-page header pill so the two views can
      // never drift. See PipelineStatus.js for the resolution logic.
      render: (p) => (
        <PipelineStatus
          bundleStatus={p.bundleStatus}
          warmEnabled={p.warmEnabled}
          variant="table"
        />
      ),
    },
    {
      header: 'VERSION',
      key: 'version',
      className: 'pipelines-col-version',
      width: '12%',
      skeleton: 'short',
      render: (p) => <span className="pipelines-version">v{p.version || 0}</span>,
    },
    {
      header: 'LAST DEPLOYED',
      key: 'updatedAt',
      className: 'pipelines-col-deployed',
      width: '18%',
      render: (p) => <span className="pipelines-deployed">{p.updatedAt ? timeAgo(p.updatedAt) : '—'}</span>,
    },
  ];

  // Pipeline row actions — text-only menu items (no icons) so the menu
  // reads as a clean list. "Activities" matches the detail page's
  // Activity tab label.
  const actions = [
    {
      label: 'View details',
      onClick: (p) => navigate(`/projects/${projectId}/pipelines/${p.workflowType}`),
    },
    {
      label: 'Activities',
      onClick: (p) => navigate(`/projects/${projectId}/pipelines/${p.workflowType}?tab=activity`),
    },
    {
      label: 'Rename',
      // Open the rename modal pre-filled with the current label (displayName
      // when set, else the slug). The actual PATCH runs in confirmRename.
      onClick: (p) => {
        setRenameTarget(p);
        setRenameDraft(p.displayName || p.workflowType || '');
      },
    },
    { divider: true },
    {
      label: 'Delete',
      danger: true,
      // Open the styled confirm modal instead of a native window.confirm —
      // the actual delete runs in confirmDelete once the user confirms.
      onClick: (p) => setDeleteTarget(p),
    },
  ];

  const confirmDelete = async () => {
    const p = deleteTarget;
    if (!p) return;
    if (!p.uuid) {
      setToast?.({ isOpen: true, message: 'Cannot delete: agent has no UUID yet (deploy it first)', color: 'danger' });
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      // Backend wires DELETE on `/workflows/{uuid}`, NOT
      // `/projects/{id}/workflows/{type}`. The handler looks the
      // workflow up via the UUID GSI then verifies the project
      // membership server-side, so the projectId is implied.
      // The mutation's onSuccess invalidates the project's agent LIST (row
      // leaves the table — React Query keeps the existing rows visible during
      // the background refetch, so no skeleton blink) AND removes the agent's
      // cached DETAIL so a stale ghost can't be served.
      await deleteAgent.mutateAsync({ uuid: p.uuid, workflowType: p.workflowType });
      setToast?.({ isOpen: true, message: `Agent "${p.workflowType}" deleted`, color: 'success' });
      setDeleteTarget(null);
    } catch (err) {
      setToast?.({ isOpen: true, message: `Delete failed: ${err.message}`, color: 'danger' });
    } finally {
      setDeleting(false);
    }
  };

  // Rename = PATCH /workflows/{uuid} { displayName }. Mirrors the detail
  // page's inline pencil-edit: an emptied/cleared name sends displayName:null
  // to reset back to the workflowType slug. Client-side 1..80 char validation
  // matches the server (workflows.js patchWorkflow).
  const confirmRename = async () => {
    const p = renameTarget;
    if (!p) return;
    if (!p.uuid) {
      setToast?.({ isOpen: true, message: 'Cannot rename: agent has no UUID yet (deploy it first)', color: 'danger' });
      setRenameTarget(null);
      return;
    }
    const trimmed = (renameDraft || '').trim();
    if (trimmed.length > 80) {
      setToast?.({ isOpen: true, message: 'Name must be 80 characters or fewer', color: 'danger' });
      return;
    }
    // Empty → reset to the slug (server clears displayName on null).
    const nextDisplayName = trimmed === '' ? null : trimmed;
    setRenaming(true);
    try {
      await api.patch(`/workflows/${p.uuid}`, { displayName: nextDisplayName });
      // Optimistic in-place cache update — no invalidate/refetch (avoids the
      // table skeleton flash). The slug/icon are unchanged; only the label
      // moves, so we patch the cached list row directly.
      queryClient.setQueryData(queryKeys.agents.list(projectId), (prev) =>
        Array.isArray(prev)
          ? prev.map((x) => (x.uuid === p.uuid ? { ...x, displayName: nextDisplayName || undefined } : x))
          : prev);
      setToast?.({ isOpen: true, message: `Renamed to "${nextDisplayName || p.workflowType}"`, color: 'success' });
      setRenameTarget(null);
    } catch (err) {
      setToast?.({ isOpen: true, message: `Rename failed: ${err.message}`, color: 'danger' });
    } finally {
      setRenaming(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Agents · Zibby</title>
      </Helmet>
      {/* When empty: return ONLY the .pipelines-empty block — no outer
          .pipelines-page wrapper. Test Runs does the same so we match
          padding/positioning exactly. The .pipelines-page wrapper (with
          24px 32px padding) only fires when there's a list to render. */}
      {!loading && pipelines.length === 0 ? (
        <div className="pipelines-empty">
          <DemoCarousel slides={[() => <McpDemo context="pipeline" />, () => <WorkflowDemo />]} />
        </div>
      ) : (
        <div className="pipelines-page">
          {/* Show the header during loading too (not just when data is in) so
              the skeleton state keeps the title + subtitle — no layout shift
              when rows land. The empty state is the other branch above. */}
          {(loading || pipelines.length > 0) && (
            <div className="pipelines-header">
              {/* Title row — page title on the left, optional static-egress
                  pill on the right. Pill only renders when the project has
                  the paid dedicated-egress addon active AND has opted in.
                  Click the pill to copy the IP. */}
              <div className="pipelines-header-row">
                <h1 className="pipelines-title">Agents</h1>
                {egressIp && (
                  <button
                    type="button"
                    className="pipelines-egress-pill"
                    onClick={() => {
                      navigator.clipboard?.writeText(egressIp).then(() => {
                        setToast?.({ isOpen: true, message: `Copied ${egressIp}`, color: 'success' });
                      });
                    }}
                    title="Copy static egress IP"
                  >
                    <IonIcon icon={globeOutline} className="pipelines-egress-pill-icon" />
                    <span className="pipelines-egress-pill-label">Static egress</span>
                    <code className="pipelines-egress-pill-ip">{egressIp}</code>
                    <IonIcon icon={copyOutline} className="pipelines-egress-pill-copy" />
                  </button>
                )}
              </div>
              <p className="pipelines-subtitle">
                Deployed agents for this project — trigger via CLI, webhook, or schedule.
                {' '}Deploy a new one from the <Link to={projectWorkflowMarketplacePath(projectId)} className="pipelines-subtitle-link">marketplace</Link>.
              </p>
            </div>
          )}

          <DataTable
            data={pipelines}
            columns={columns}
            loading={loading}
            rowKey="workflowType"
            actions={actions}
            onRowClick={(p) => navigate(`/projects/${projectId}/pipelines/${p.workflowType}`)}
          />
        </div>
      )}

      <DestroyAppModal
        open={!!deleteTarget}
        label={deleteTarget?.workflowType}
        title={deleteTarget ? `Delete agent ${deleteTarget.workflowType}?` : ''}
        warning={(
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 1.55, marginTop: 0, marginBottom: 12 }}>
            Permanently deletes this agent.{' '}
            <strong style={{ color: '#fff' }}>This cannot be undone.</strong>
          </p>
        )}
        confirmLabel="Delete agent"
        busyLabel="Deleting…"
        busy={deleting}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={confirmDelete}
      />

      {/* Rename modal — small dark dialog with a single text input. Built on
          MktAlertModal (same base SoloDestroyModal uses) with secondaryLabel
          suppressed so we render our own Save/Cancel row. */}
      <MktAlertModal
        open={!!renameTarget}
        onClose={renaming ? undefined : () => setRenameTarget(null)}
        title={renameTarget ? `Rename "${renameTarget.displayName || renameTarget.workflowType}"` : ''}
        ariaLabel="Rename agent"
        secondaryLabel=""
      >
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.5, marginTop: 0, marginBottom: 8 }}>
          Display name (clear to reset to <code style={{ color: '#fff' }}>{renameTarget?.workflowType}</code>):
        </p>
        <input
          type="text"
          value={renameDraft}
          onChange={(e) => setRenameDraft(e.target.value)}
          disabled={renaming}
          autoFocus
          maxLength={80}
          spellCheck={false}
          aria-label="Agent display name"
          placeholder={renameTarget?.workflowType}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '10px 12px',
            marginBottom: 16,
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.16)',
            background: 'rgba(0,0,0,0.3)',
            color: '#fff',
            fontSize: 14,
            fontFamily: 'inherit',
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !renaming) confirmRename(); }}
        />
        <div className="mkt-alert-modal-actions">
          <button
            type="button"
            className="mkt-modal-deploy"
            disabled={renaming}
            onClick={confirmRename}
          >
            {renaming ? 'Saving…' : 'Save'}
          </button>
          {!renaming && (
            <button
              type="button"
              className="destroy-modal-cancel"
              onClick={() => setRenameTarget(null)}
            >
              Cancel
            </button>
          )}
        </div>
      </MktAlertModal>
    </>
  );
};

export default PipelinesPage;
